/**
 * Themes, brand kit, colour tokens and text styles shared by pages, slides and document templates
 * (plan §7.3, D9; `audit/slides-builder.md` §4.9).
 *
 * Elements store colours as `ColorRef`: either a theme token (`{ token: 'accent' }`) or an explicit
 * hex (`{ hex: '#444CE7' }`). Switching the theme re-resolves tokens only; an explicit hex or an
 * explicitly chosen font is never overwritten. This replaces the hex-remap of `themeCanvasFrame`.
 *
 * Owner: doc-engine (wave 1), design-system (wave 2).
 */
import { z } from 'zod';
import { LocalizedText } from '../localized-text';
import {
  Hex,
  MAX_BRAND_COLORS,
  NAMED_COLORS,
  THEME_COLOR_ROLES,
  TextAlign,
  contrastRatio,
  isTokenColor,
  ColorRef,
  type NamedColor,
  type ThemeColorRole,
} from './color';

export * from './color';

// -------------------------------------------------------------------------------------------------
// Fonts
// -------------------------------------------------------------------------------------------------

/** OFL fonts bundled with the product (doc-render ships the files; plan §6, D19). */
export const BUNDLED_FONTS = [
  'Geist',
  'Geist Mono',
  'Inter',
  'IBM Plex Sans',
  'IBM Plex Serif',
  'Source Serif 4',
  'DM Sans',
  'Space Grotesk',
  'Playfair Display',
  'JetBrains Mono',
  'Noto Sans',
  'Liberation Sans',
  'Liberation Serif',
  'Liberation Mono',
  'Gelasio',
  'Carlito',
  'Caladea',
] as const;
export type BundledFont = (typeof BUNDLED_FONTS)[number];

/**
 * Legacy and office font names that stay valid in stored content. Each renders with a bundled,
 * metric-compatible substitute, so line breaks do not move.
 */
export const FONT_SUBSTITUTES = {
  Arial: 'Liberation Sans',
  Helvetica: 'Liberation Sans',
  'Times New Roman': 'Liberation Serif',
  'Courier New': 'Liberation Mono',
  Georgia: 'Gelasio',
  Calibri: 'Carlito',
  Cambria: 'Caladea',
} as const satisfies Record<string, BundledFont>;
export type FontAlias = keyof typeof FONT_SUBSTITUTES;

const CUSTOM_FONT_RE = /^custom:[a-z0-9][a-z0-9-]{0,59}$/;
/** A bundled family, a legacy alias, or a workspace font uploaded by an admin (`custom:<slug>`). */
export const FontFamily = z.union([
  z.enum(BUNDLED_FONTS),
  z.enum(Object.keys(FONT_SUBSTITUTES) as [FontAlias, ...FontAlias[]]),
  z.string().regex(CUSTOM_FONT_RE, { message: 'Unknown font' }),
]);
export type FontFamily = z.infer<typeof FontFamily>;

/** The family that actually renders a font name (aliases map to their metric-compatible substitute). */
export function renderedFontFamily(font: string): string {
  return (FONT_SUBSTITUTES as Record<string, string>)[font] ?? font;
}

/** A font role of the theme (`@heading`, `@body`, `@mono`) or an explicit family. */
export const FontRef = z.union([z.enum(['@heading', '@body', '@mono']), FontFamily]);
export type FontRef = z.infer<typeof FontRef>;

// -------------------------------------------------------------------------------------------------
// Text styles
// -------------------------------------------------------------------------------------------------

export const TEXT_STYLE_KEYS = ['title', 'heading', 'subheading', 'body', 'caption', 'label'] as const;
export const TextStyleKey = z.enum(TEXT_STYLE_KEYS);
export type TextStyleKey = z.infer<typeof TextStyleKey>;

export const TextStyle = z
  .object({
    font: FontRef,
    /** CSS pixels at 96 dpi (slides) or points (document templates); the surface defines the unit. */
    size: z.number().min(4).max(400),
    weight: z.number().int().min(100).max(900).multipleOf(100),
    lineHeight: z.number().min(0.8).max(3),
    /** em */
    letterSpacing: z.number().min(-0.1).max(0.5).default(0),
    italic: z.boolean().default(false),
    transform: z.enum(['none', 'uppercase']).default('none'),
    color: ColorRef,
    /** Language-aware hyphenation (doc-render); justified text hyphenates unless this is false. */
    hyphenate: z.boolean().optional(),
  })
  .strict();
export type TextStyle = z.output<typeof TextStyle>;

/** A text style used by an element: the theme style plus explicit overrides (which always win). */
export const TextStyleRef = z
  .object({
    ref: TextStyleKey,
    overrides: TextStyle.partial().strict().optional(),
    align: TextAlign.optional(),
  })
  .strict();
export type TextStyleRef = z.output<typeof TextStyleRef>;

// -------------------------------------------------------------------------------------------------
// Theme and brand kit
// -------------------------------------------------------------------------------------------------

const ThemeKeySlug = z
  .string()
  .min(2)
  .max(60)
  .regex(/^[a-z][a-z0-9-]*$/, { message: 'Use lower-case letters, digits and dashes' });

export const ThemeColors = z.object(
  Object.fromEntries(THEME_COLOR_ROLES.map((role) => [role, Hex])) as Record<ThemeColorRole, typeof Hex>,
);
export type ThemeColors = Record<ThemeColorRole, string>;

export const NamedColorValues = z.object({ text: Hex, background: Hex }).strict();
export type NamedColorValues = z.infer<typeof NamedColorValues>;

const TextStyles = z
  .object(
    Object.fromEntries(TEXT_STYLE_KEYS.map((k) => [k, TextStyle])) as Record<TextStyleKey, typeof TextStyle>,
  )
  .strict();

/** Print sizes (pt) used when a theme defines no print text styles. */
export const DEFAULT_PRINT_SIZES: Record<TextStyleKey, number> = {
  title: 26,
  heading: 16,
  subheading: 12.5,
  body: 10,
  caption: 8,
  label: 7.5,
};

/** Where text is set: screen (px) or print (pt). */
export type TextSurface = 'screen' | 'print';

export const Theme = z
  .object({
    key: ThemeKeySlug,
    name: LocalizedText,
    description: LocalizedText.optional(),
    /** True when `background` is dark; renderers pick light text for named colours. */
    dark: z.boolean().default(false),
    colors: ThemeColors.strict(),
    /** Brand colours `brand.0` … `brand.7`. */
    palette: z.array(Hex).max(MAX_BRAND_COLORS).default([]),
    /** Overrides of the named colours; missing ones use `defaultNamedColors(dark)`. */
    namedColors: z.partialRecord(z.enum(NAMED_COLORS), NamedColorValues).optional(),
    fonts: z.object({ heading: FontFamily, body: FontFamily, mono: FontFamily }).strict(),
    /** Text styles of screen surfaces (slides, designs); sizes in CSS pixels. */
    textStyles: TextStyles,
    /** Text styles of print surfaces (document templates); sizes in points. Derived when absent. */
    printTextStyles: TextStyles.optional(),
    /** Default corner radius in px. */
    radius: z.number().min(0).max(48),
    /** Series colours of charts, in order. */
    chartPalette: z.array(Hex).min(3).max(12),
  })
  .strict();
export type Theme = z.output<typeof Theme>;
export type ThemeInput = z.input<typeof Theme>;

export const AssetId = z.uuid();

/** A stored theme key (`graphite`, `workspace-brand`); `ThemeKey` of `./design` is the legacy design-theme enum. */
export const StoredThemeKey = ThemeKeySlug;

/** Surfaces whose new artefacts start in the brand kit's default theme (plan §9.1). */
export const BRAND_SURFACES = ['deck', 'documentTemplate', 'publicPage'] as const;
export const BrandSurface = z.enum(BRAND_SURFACES);
export type BrandSurface = z.infer<typeof BrandSurface>;

/** Logo variants: for light backgrounds, for dark backgrounds, and the square mark (favicons, avatars). */
export const BRAND_LOGO_VARIANTS = ['primary', 'onDark', 'mark'] as const;
export type BrandLogoVariant = (typeof BRAND_LOGO_VARIANTS)[number];

/**
 * Workspace brand kit (metadata, plan §9.1; `audit/slides-builder.md` §4.9). The Branding settings
 * edit it; saving it regenerates the workspace's brand theme (`brandTheme`, stored with the key
 * `BRAND_THEME_KEY`), which new decks, document templates and public pages start from (`defaults`).
 */
export const BrandKit = z
  .object({
    logos: z
      .object({ primary: AssetId.optional(), mark: AssetId.optional(), onDark: AssetId.optional() })
      .strict()
      .default({}),
    colors: z
      .array(z.object({ name: LocalizedText, hex: Hex }).strict())
      .max(MAX_BRAND_COLORS)
      .default([]),
    /** The brand colour that becomes the brand theme's accent (index into `colors`). */
    accent: z
      .number()
      .int()
      .min(0)
      .max(MAX_BRAND_COLORS - 1)
      .optional(),
    fonts: z.object({ heading: FontFamily.optional(), body: FontFamily.optional() }).strict().default({}),
    radius: z.number().min(0).max(48).optional(),
    chartPalette: z.array(Hex).min(3).max(12).optional(),
    /** The theme the brand theme starts from (background, ink, text styles); default: the first starter theme. */
    baseTheme: StoredThemeKey.optional(),
    /** The theme new artefacts of each surface start in (a theme key; usually the brand theme). */
    defaults: z
      .object({
        deck: StoredThemeKey.optional(),
        documentTemplate: StoredThemeKey.optional(),
        publicPage: StoredThemeKey.optional(),
      })
      .strict()
      .default({}),
    /**
     * The app itself: `accent` (default on) paints buttons, links, selection and focus of the app
     * shell, the sign-in page and public forms in the brand accent instead of the product indigo.
     */
    app: z.object({ accent: z.boolean().optional() }).strict().optional(),
  })
  .strict()
  .refine((k) => k.accent === undefined || k.accent < k.colors.length, {
    message: 'The accent must be one of the brand colours',
    path: ['accent'],
  });
export type BrandKit = z.output<typeof BrandKit>;
export type BrandKitInput = z.input<typeof BrandKit>;

/** The key of the theme the brand kit maintains in every workspace that has one. */
export const BRAND_THEME_KEY = 'workspace-brand';

/** The CSS family of a workspace font `custom:<slug>` (the same name `@dataroom/fonts` renders). */
export function customFontCssFamily(slug: string): string {
  return `dr-custom-${slug}`;
}

/** The slug of a workspace font value (`custom:acme-sans` → `acme-sans`), or null for other fonts. */
export function customFontSlug(font: string | undefined | null): string | null {
  const m = /^custom:([a-z0-9][a-z0-9-]{0,59})$/.exec(font ?? '');
  return m ? m[1]! : null;
}

/** One uploaded face of a workspace font, as `@font-face` needs it. */
export type FontFaceSource = {
  slug: string;
  weight: number;
  style: 'normal' | 'italic';
  format: 'woff2' | 'woff' | 'truetype' | 'opentype';
  url: string;
};

/**
 * `@font-face` rules for workspace fonts (web app, public pages and print): each face under the CSS
 * family `dr-custom-<slug>`, with `font-display: swap` so text shows at once in the fallback.
 */
export function fontFaceCss(faces: readonly FontFaceSource[]): string {
  return faces
    .map(
      (f) =>
        `@font-face{font-family:"${customFontCssFamily(f.slug)}";src:url("${f.url.replace(/"/g, '%22')}") format("${f.format}");font-weight:${f.weight};font-style:${f.style};font-display:swap}`,
    )
    .join('\n');
}

/** Mix two colours (`amount` of `b`, 0…1), as `#RRGGBB`. */
export function mixHex(a: string, b: string, amount: number): string {
  const ch = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  const out = [0, 1, 2].map((i) => Math.round(ch(a, i) * (1 - amount) + ch(b, i) * amount));
  return `#${out.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/**
 * The brand theme: the base theme with the kit's palette, fonts, radius and chart colours (the kit
 * wins), the accent colour (and its soft tint) when the kit names one, and charts in brand colours
 * when the kit has three or more and no chart palette of its own.
 */
export function brandTheme(
  base: Theme,
  brand: BrandKit,
  identity: { key: string; name: LocalizedText; description?: LocalizedText },
): Theme {
  const branded = applyBrandKit(base, brand);
  const accent = brand.accent !== undefined ? brand.colors[brand.accent]?.hex : undefined;
  const colors = accent
    ? {
        ...branded.colors,
        accent,
        soft: mixHex(accent, branded.colors.background, base.dark ? 0.8 : 0.9),
      }
    : branded.colors;
  const brandHexes = [...new Set(brand.colors.map((c) => c.hex.toUpperCase()))];
  const chartPalette =
    brand.chartPalette ??
    (brandHexes.length >= 3
      ? brandHexes
      : [...new Set([...brandHexes, ...base.chartPalette.map((h) => h.toUpperCase())])].slice(0, 12));
  const { description: _base, ...rest } = branded;
  return Theme.parse({
    ...rest,
    colors,
    chartPalette,
    key: identity.key,
    name: identity.name,
    ...(identity.description ? { description: identity.description } : {}),
  });
}

/** WCAG level of a contrast ratio for normal text: `AAA` ≥ 7, `AA` ≥ 4.5, `AA large` ≥ 3, else `fail`. */
export type ContrastLevel = 'AAA' | 'AA' | 'AA-large' | 'fail';
export function contrastLevel(ratio: number): ContrastLevel {
  return ratio >= 7 ? 'AAA' : ratio >= 4.5 ? 'AA' : ratio >= 3 ? 'AA-large' : 'fail';
}

export type BrandColorHints = {
  /** The colour as text (or thin lines) on the theme's light surface. */
  onLight: { ratio: number; level: ContrastLevel };
  /** The colour as text on a dark surface. */
  onDark: { ratio: number; level: ContrastLevel };
  /** The text colour that reads best on top of it (white or ink), and its contrast. */
  textOn: { color: string; ratio: number; level: ContrastLevel };
};

/**
 * Contrast hints for a brand colour (the Branding settings show them next to each swatch): how it
 * reads as text on light and dark surfaces and which text colour to put on it.
 */
export function brandColorHints(hex: string, surfaces: { light?: string; dark?: string; ink?: string } = {}) {
  const light = surfaces.light ?? '#FFFFFF';
  const dark = surfaces.dark ?? '#0C0E14';
  const ink = surfaces.ink ?? '#101828';
  const onLight = contrastRatio(hex, light);
  const onDark = contrastRatio(hex, dark);
  const white = contrastRatio(hex, '#FFFFFF');
  const inked = contrastRatio(hex, ink);
  const best = white >= inked ? { color: '#FFFFFF', ratio: white } : { color: ink, ratio: inked };
  const round = (r: number) => Math.round(r * 100) / 100;
  return {
    onLight: { ratio: round(onLight), level: contrastLevel(onLight) },
    onDark: { ratio: round(onDark), level: contrastLevel(onDark) },
    textOn: { color: best.color, ratio: round(best.ratio), level: contrastLevel(best.ratio) },
  } satisfies BrandColorHints;
}

/** The accent tokens of the UI kit (`packages/ui` tokens.css) a brand accent replaces. */
export const ACCENT_TOKENS = [
  '--accent',
  '--accent-hover',
  '--accent-ink',
  '--accent-ink-strong',
  '--accent-tint',
  '--accent-soft',
  '--accent-border',
  '--focus-ring',
  '--on-accent',
  '--selection-tint',
] as const;
export type AccentTokens = Record<(typeof ACCENT_TOKENS)[number], string>;

/** The UI kit's surfaces the accent tokens are measured against (light and dark `--surface`). */
export const UI_SURFACE = { light: '#FFFFFF', dark: '#161922' } as const;
/** The product accent: the accent of workspaces whose brand kit names none. */
export const PRODUCT_ACCENT = '#444CE7';
/** Colours Settings › Branding offers for a new brand colour, in this order, before it falls back to the product accent. */
export const BRAND_COLOR_SUGGESTIONS = [
  '#0B6E6E',
  '#F2A900',
  '#14213D',
  '#E4572E',
  '#76B041',
  '#7A5AF8',
  '#2E90FA',
  '#D92D20',
] as const;
const UI_INK = '#101828';

/** Move a colour toward `toward` in 5 % steps until it reaches `ratio` against `surface`. */
function withContrast(hex: string, surface: string, ratio: number, toward: string): string {
  let out = hex.toUpperCase();
  for (let i = 1; i <= 20 && contrastRatio(out, surface) < ratio; i++) out = mixHex(hex, toward, i * 0.05);
  return out;
}

/**
 * The UI kit's accent tokens for a brand accent, in the light and the dark theme: fills keep the brand
 * colour where it reaches 3:1 against the surface (else the nearest shade that does), accent text
 * reaches 4.5:1, text on the fill is white or ink (whichever reads better), and tints, borders and the
 * focus ring are mixed from the colour and the surface. The app shell, the sign-in page and public
 * forms set them when the kit's `app.accent` is on.
 */
export function brandAccentTokens(hex: string): { light: AccentTokens; dark: AccentTokens } {
  const scheme = (surface: string, dark: boolean): AccentTokens => {
    const away = dark ? '#FFFFFF' : '#000000';
    const fill = withContrast(hex, surface, 3, away);
    const tint = mixHex(hex, surface, dark ? 0.8 : 0.9);
    const soft = mixHex(hex, surface, dark ? 0.87 : 0.95);
    // Accent text sits on the surface and on the accent tints (selected choices, hovered links): it
    // reaches 4.5:1 on every one of them, so no combination falls below AA in either theme.
    let ink = withContrast(hex, surface, 4.5, away);
    for (const bg of [tint, soft]) if (contrastRatio(ink, bg) < 4.5) ink = withContrast(ink, bg, 4.5, away);
    return {
      '--accent': fill,
      '--accent-hover': mixHex(fill, '#000000', dark ? 0.1 : 0.14),
      '--accent-ink': ink,
      '--accent-ink-strong': mixHex(ink, away, 0.2),
      '--accent-tint': tint,
      '--accent-soft': soft,
      '--accent-border': mixHex(hex, surface, dark ? 0.5 : 0.72),
      '--focus-ring': mixHex(hex, surface, dark ? 0.6 : 0.82),
      '--on-accent': brandColorHints(fill, { ink: UI_INK }).textOn.color,
      '--selection-tint': `${fill}${dark ? '33' : '1F'}`,
    };
  };
  return { light: scheme(UI_SURFACE.light, false), dark: scheme(UI_SURFACE.dark, true) };
}

/** The kit's accent hex, when it names one and `app.accent` is not switched off; else null. */
export function brandAppAccent(kit: BrandKit | null | undefined): string | null {
  if (!kit || kit.app?.accent === false || kit.accent === undefined) return null;
  return kit.colors[kit.accent]?.hex ?? null;
}

/** The theme key new artefacts of a surface start in (the kit's default, else `fallback`). */
export function brandDefaultThemeKey(
  brand: BrandKit | null | undefined,
  surface: BrandSurface,
  fallback: string,
): string {
  return brand?.defaults[surface] ?? fallback;
}

/** The brand kit wins for palette, fonts, radius and chart colours; roles stay the theme's. */
export function applyBrandKit(theme: Theme, brand: BrandKit): Theme {
  return {
    ...theme,
    palette: brand.colors.length ? brand.colors.map((c) => c.hex) : theme.palette,
    fonts: {
      ...theme.fonts,
      ...(brand.fonts.heading ? { heading: brand.fonts.heading } : {}),
      ...(brand.fonts.body ? { body: brand.fonts.body } : {}),
    },
    radius: brand.radius ?? theme.radius,
    chartPalette: brand.chartPalette ?? theme.chartPalette,
  };
}

const LIGHT_NAMED: Record<NamedColor, NamedColorValues> = {
  gray: { text: '#667085', background: '#F2F4F7' },
  brown: { text: '#8A5A3B', background: '#F6EEE8' },
  orange: { text: '#C4520F', background: '#FDF0E6' },
  yellow: { text: '#A16207', background: '#FEF7D6' },
  green: { text: '#15803D', background: '#E8F6EC' },
  blue: { text: '#1D63C9', background: '#E7F0FD' },
  purple: { text: '#6E4FD3', background: '#F1ECFD' },
  pink: { text: '#BE2E77', background: '#FCEBF3' },
  red: { text: '#C9302C', background: '#FDECEB' },
};
const DARK_NAMED: Record<NamedColor, NamedColorValues> = {
  gray: { text: '#A4ACB9', background: '#2B303B' },
  brown: { text: '#D3A27F', background: '#3A2E27' },
  orange: { text: '#F5A061', background: '#3F2B1E' },
  yellow: { text: '#F2CF5B', background: '#3B3420' },
  green: { text: '#6FD08F', background: '#1F3628' },
  blue: { text: '#7FB0F5', background: '#1E2E45' },
  purple: { text: '#B39CF5', background: '#2D2647' },
  pink: { text: '#F08DBD', background: '#3F2333' },
  red: { text: '#F38B86', background: '#422424' },
};
export function defaultNamedColors(dark: boolean): Record<NamedColor, NamedColorValues> {
  return dark ? DARK_NAMED : LIGHT_NAMED;
}

export type ColorUse = 'text' | 'background' | 'fill';

/**
 * Resolve a colour reference against a theme. Explicit hex values pass through unchanged. A brand
 * token outside the palette resolves to the accent colour, so content never renders invisible.
 */
export function resolveColor(ref: ColorRef, theme: Theme, use: ColorUse = 'fill'): string {
  if (!isTokenColor(ref)) return ref.hex;
  const token = ref.token as string;
  if ((THEME_COLOR_ROLES as readonly string[]).includes(token)) return theme.colors[token as ThemeColorRole];
  if ((NAMED_COLORS as readonly string[]).includes(token)) {
    const named =
      theme.namedColors?.[token as NamedColor] ?? defaultNamedColors(theme.dark)[token as NamedColor];
    return use === 'background' ? named.background : named.text;
  }
  const index = Number(token.slice('brand.'.length));
  return theme.palette[index] ?? theme.colors.accent;
}

export function resolveFont(font: FontRef, theme: Theme): string {
  if (font === '@heading') return theme.fonts.heading;
  if (font === '@body') return theme.fonts.body;
  if (font === '@mono') return theme.fonts.mono;
  return font;
}

/** The theme's style for a surface (print styles fall back to the screen style at print sizes). */
export function themeTextStyle(theme: Theme, key: TextStyleKey, surface: TextSurface = 'screen'): TextStyle {
  if (surface === 'screen') return theme.textStyles[key];
  return theme.printTextStyles?.[key] ?? { ...theme.textStyles[key], size: DEFAULT_PRINT_SIZES[key] };
}

export type ResolvedTextStyle = {
  fontFamily: string;
  size: number;
  weight: number;
  lineHeight: number;
  letterSpacing: number;
  italic: boolean;
  transform: 'none' | 'uppercase';
  color: string;
  align: TextAlign;
};

/** The concrete style of a text: theme style, then overrides; explicit fonts and hex colours stay. */
export function resolveTextStyle(
  style: TextStyleRef,
  theme: Theme,
  surface: TextSurface = 'screen',
): ResolvedTextStyle {
  const base = themeTextStyle(theme, style.ref, surface);
  const merged = { ...base, ...(style.overrides ?? {}) };
  return {
    fontFamily: resolveFont(merged.font, theme),
    size: merged.size,
    weight: merged.weight,
    lineHeight: merged.lineHeight,
    letterSpacing: merged.letterSpacing ?? 0,
    italic: merged.italic ?? false,
    transform: merged.transform ?? 'none',
    color: resolveColor(merged.color, theme, 'text'),
    align: style.align ?? 'left',
  };
}
